import { injectProcessBudgetAttributeChanges } from '../mutations/balance-sheet-account-mutations';
import {
  BalanceSheetAccountDetail,
  BalanceSheetEntitySettingsPayload,
  PendingGridChanges,
} from '../new-manage-balance-sheets-accounts-component-modal.component';

export interface ExecuteSaveParams {
  notes: string;
  pendingChanges: PendingGridChanges;
  processBudgetAttributeChangesMutation: ReturnType<typeof injectProcessBudgetAttributeChanges>;
  supportingDocumentS3BucketKeys: string[];
  vendorIds: string[];
}

export function mergePendingChangesIntoVendorSettings({
  allVendorIds,
  allVendorSettings,
  applyToAll,
  notes,
  pendingChanges,
  selectedVendorId,
  supportingDocumentS3BucketKeys,
}: {
  allVendorIds: string[];
  allVendorSettings: Record<string, BalanceSheetEntitySettingsPayload>;
  applyToAll: boolean;
  notes: string;
  pendingChanges: PendingGridChanges;
  selectedVendorId: string;
  supportingDocumentS3BucketKeys: string[];
}): {
  meta_data: string;
  payload: Record<string, BalanceSheetEntitySettingsPayload>;
} {
  const result: Record<string, BalanceSheetEntitySettingsPayload> = {};

  for (const vendorId of allVendorIds) {
    let newVendorSettings: BalanceSheetEntitySettingsPayload;

    if (vendorId === selectedVendorId) {
      newVendorSettings = { ...pendingChanges.entitySettingsToSave };
    } else if (applyToAll) {
      const selectedSettings = pendingChanges.entitySettingsToSave;
      const currentTargetVendorSettings = allVendorSettings[vendorId] || {};
      const copiedSettings = copyDataExceptPO(selectedSettings, currentTargetVendorSettings);

      newVendorSettings = applyColumnStructureChanges(copiedSettings, pendingChanges);
    } else {
      const currentVendorSettings = allVendorSettings[vendorId] || {};

      newVendorSettings = applyColumnStructureChanges({ ...currentVendorSettings }, pendingChanges);
    }

    result[vendorId] = newVendorSettings;
  }

  return {
    meta_data: JSON.stringify({
      ADDED_COLUMNS: pendingChanges.newBudgetAttributes.map((a) => a.name),
      BUCKET_KEYS: supportingDocumentS3BucketKeys,
      NOTES: notes,
      REMOVED_COLUMNS: pendingChanges.removedBudgetAttributes.map((a) => a.name),
      RENAMED_COLUMNS: pendingChanges.renamedBudgetAttributes.map((a) => ({
        from: a.name,
        to: a.new_name,
      })),
    }),
    payload: result,
  };
}

function applyColumnStructureChanges(
  settings: BalanceSheetEntitySettingsPayload,
  pending: PendingGridChanges
): BalanceSheetEntitySettingsPayload {
  const { newBudgetAttributes, removedBudgetAttributes, renamedBudgetAttributes } = pending;
  const newCols = newBudgetAttributes.map((a) => a.name);
  const removedCols = removedBudgetAttributes.map((a) => a.name);
  const renamedCols = renamedBudgetAttributes.map((a) => ({ from: a.name, to: a.new_name }));

  const newSettingsAccounts: Record<string, BalanceSheetAccountDetail> = {};

  for (const key in settings) {
    if (
      key !== 'NOTES' &&
      key !== 'BUCKET_KEYS' &&
      Object.prototype.hasOwnProperty.call(settings, key)
    ) {
      const accountDetail = settings[key] as BalanceSheetAccountDetail;

      newSettingsAccounts[key] = JSON.parse(JSON.stringify(accountDetail));
    }
  }

  for (const accountType of Object.keys(newSettingsAccounts)) {
    const row = newSettingsAccounts[accountType];

    if (!row.attributes) {
      row.attributes = {};
    }

    for (const col of newCols) {
      if (!(col in row.attributes)) {
        row.attributes[col] = '';
      }
    }

    for (const col of removedCols) {
      delete row.attributes[col];
    }

    for (const { from, to } of renamedCols) {
      if (from in row.attributes) {
        row.attributes[to] = row.attributes[from];
        delete row.attributes[from];
      }
    }
  }

  const finalSettings: BalanceSheetEntitySettingsPayload = { ...newSettingsAccounts };

  if (settings.NOTES !== undefined) {
    finalSettings.NOTES = settings.NOTES;
  }

  if (settings.BUCKET_KEYS !== undefined) {
    finalSettings.BUCKET_KEYS = settings.BUCKET_KEYS;
  }

  return finalSettings;
}

function copyDataExceptPO(
  selectedSettings: BalanceSheetEntitySettingsPayload,
  targetSettingsForPO: BalanceSheetEntitySettingsPayload
): BalanceSheetEntitySettingsPayload {
  const result: BalanceSheetEntitySettingsPayload = JSON.parse(JSON.stringify(selectedSettings));

  for (const accountType in result) {
    if (
      !Object.prototype.hasOwnProperty.call(result, accountType) ||
      accountType === 'NOTES' ||
      accountType === 'BUCKET_KEYS'
    ) {
      continue;
    }

    const selectedAccountDetail = result[accountType] as BalanceSheetAccountDetail;
    const targetAccountDetail = targetSettingsForPO
      ? (targetSettingsForPO[accountType] as BalanceSheetAccountDetail | undefined)
      : undefined;

    if (!selectedAccountDetail.attributes) {
      selectedAccountDetail.attributes = {};
    }

    // Preserve PO # from the target vendor's corresponding account type
    if (
      targetAccountDetail &&
      targetAccountDetail.attributes &&
      'PO #' in targetAccountDetail.attributes
    ) {
      selectedAccountDetail.attributes['PO #'] = targetAccountDetail.attributes['PO #'];
    } else {
      // If PO# not in target, or target account/attributes don't exist, ensure it's not on selected's account.
      delete selectedAccountDetail.attributes['PO #'];
    }
  }

  return result;
}
